import type { ReactElement } from "react";

import { useNavigate } from "react-router-dom";
import PageLayout from "@/components/PageLayout";
import {
  XCircle,
  ArrowLeft,
  CreditCard,
  HelpCircle,
  Sparkles,
} from "lucide-react";

/**
 * PaymentCancelPage Component
 *
 * Displays payment cancellation message with options to retry.
 */
const PaymentCancelPage = (): ReactElement => {
  const navigate = useNavigate();

  return (
    <PageLayout>
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full">
          {/* Card */}
          <div
            className="rounded-3xl p-8 text-center shadow-lg"
            style={{ background: "var(--st-bg-surface)", border: "1px solid var(--st-border-light)" }}
          >
            {/* Cancel Icon */}
            <div className="mb-6">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{ background: "var(--st-bg-muted)" }}
              >
                <XCircle className="w-10 h-10" style={{ color: "var(--st-text-muted)" }} />
              </div>
            </div>

            {/* Title */}
            <h1
              className="text-2xl font-bold mb-3"
              style={{ fontFamily: "Chewy, cursive", color: "var(--st-text-heading)" }}
            >
              Payment Canceled
            </h1>

            <p
              className="text-sm mb-8"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              No worries! Your payment was not processed and you haven't been
              charged. You can try again whenever you're ready.
            </p>

            {/* Actions */}
            <div className="space-y-3">
              <button
                onClick={() => navigate("/pricing")}
                className="w-full py-3.5 px-4 rounded-xl font-bold transition-all hover:opacity-90 dark:hover:opacity-80 flex items-center justify-center gap-2"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background:
                    "linear-gradient(135deg, var(--st-primary-hover) 0%, var(--st-primary) 100%)",
                  color: "white",
                }}
              >
                <CreditCard className="w-5 h-5" />
                Try Again
              </button>

              <button
                onClick={() => navigate("/")}
                className="w-full py-3.5 px-4 rounded-xl font-semibold transition-all hover:shadow-sm dark:hover:shadow-md flex items-center justify-center gap-2"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  background: "var(--st-border-light)",
                  color: "var(--st-text-heading)",
                }}
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </button>
            </div>

            {/* Encouragement */}
            <div
              className="mt-6 pt-6 border-t flex items-center justify-center gap-2"
              style={{ borderColor: "var(--st-border-light)" }}
            >
              <Sparkles className="w-4 h-4" style={{ color: "var(--st-primary-hover)" }} />
              <span
                className="text-sm"
                style={{
                  fontFamily: "Quicksand, sans-serif",
                  color: "var(--st-text-muted)",
                }}
              >
                Magical stories await when you're ready!
              </span>
            </div>
          </div>

          {/* Help */}
          <div className="mt-6 text-center">
            <div
              className="flex items-center justify-center gap-2 text-sm"
              style={{ fontFamily: "Quicksand, sans-serif", color: "var(--st-text-muted)" }}
            >
              <HelpCircle className="w-4 h-4" />
              <span>Need help? Contact us at support@mytho-labs.com</span>
            </div>
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

export default PaymentCancelPage;
